<script setup lang="ts">
/**
 * Saisie d'un montant au pavé numérique, sans clavier système : affichage immédiat en 56 px,
 * entier uniquement, plafonné. Le clavier physique fonctionne aussi (chiffres, retour arrière).
 * Un montant déjà présent s'affiche surligné, comme sélectionné : le premier chiffre tapé le
 * remplace (sinon, modifier 2 000 en 3 500 donnerait 20 003 500).
 */
import { computed, ref } from 'vue'

import { activeCurrency, currencySpoken, currencySymbol, formatMoney, MAX_AMOUNT } from '@/domain'
import { fr } from '@/copy/fr'

import Keypad from './Keypad.vue'

const props = withDefaults(
  defineProps<{ label: string; max?: number; error?: string; compact?: boolean }>(),
  { max: MAX_AMOUNT, error: undefined, compact: false },
)

const model = defineModel<number | null>({ default: null })

/** Montant présent à l'ouverture, pas encore touché : il sera remplacé à la première saisie. */
const fresh = ref(model.value !== null)

const symbol = computed(() => currencySymbol(activeCurrency()))

const display = computed(() =>
  model.value === null ? '0' : formatMoney(model.value, { withCurrency: false }),
)

function append(digits: string) {
  const start = fresh.value ? '' : String(model.value ?? '')
  fresh.value = false
  const next = Number(`${start}${digits}`)
  if (!Number.isSafeInteger(next) || next > props.max) return
  model.value = next === 0 ? null : next
}

function backspace() {
  fresh.value = false
  const text = String(model.value ?? '')
  const next = text.slice(0, -1)
  model.value = next === '' ? null : Number(next)
}

function onKeydown(event: KeyboardEvent) {
  if (/^\d$/.test(event.key)) {
    append(event.key)
    event.preventDefault()
  } else if (event.key === 'Backspace') {
    backspace()
    event.preventDefault()
  } else if (event.key === 'Delete' || event.key === 'Escape') {
    model.value = null
  }
}
</script>

<template>
  <div :class="['flex flex-col', compact ? 'gap-3' : 'gap-6']">
    <div
      class="flex flex-col items-center gap-1 rounded-control py-2 outline-offset-4 focus-visible:outline-2"
      tabindex="0"
      role="spinbutton"
      :aria-label="label"
      :aria-valuenow="model ?? 0"
      aria-valuemin="0"
      :aria-valuemax="max"
      :aria-valuetext="`${display} ${currencySpoken(activeCurrency())}`"
      :aria-invalid="error ? 'true' : undefined"
      @keydown="onKeydown"
    >
      <span class="text-label text-ink-muted">{{ label }}</span>
      <span
        :class="[
          'flex items-baseline gap-2 text-entry tabular-nums',
          model === null ? 'text-ink-disabled' : 'text-ink',
          fresh ? 'rounded-inset bg-accent-soft px-2' : '',
        ]"
        aria-live="polite"
      >
        {{ display }}<span class="text-section font-medium text-ink-muted">{{ symbol }}</span>
      </span>
      <p v-if="error" class="text-caption text-danger" role="alert">{{ error }}</p>
      <span v-else class="sr-only">{{ fr.ui.amountKeyboardHint }}</span>
    </div>
    <Keypad
      :compact="compact"
      @digit="append"
      @backspace="backspace"
      @clear="((fresh = false), (model = null))"
    />
  </div>
</template>
